import { allFromSnapshot, oneFromSnapshot, } from "@buildwithflux/firestore-compatibility-layer";
import { lowerTimeBound, upperTimeBound, } from "./ActionRecordRepository";
export class FirestoreActionRecordRepository {
    db;
    metadataGenerator;
    clientId;
    constructor(db, metadataGenerator, clientIdProvider) {
        this.db = db;
        this.metadataGenerator = metadataGenerator;
        this.clientId = clientIdProvider.clientId;
    }
    delete(documentUid, actionRecordUid) {
        return this.db.documentActionRecordsCollection(documentUid).doc(actionRecordUid).delete().then();
    }
    // NOTE: it's a little annoying but this needs to be kept in sync with the subscribe method below w.r.t. option
    // behavior.
    async getAll(documentUid, options = {}) {
        const query = this.queryForOptions(documentUid, options);
        const predicates = this.predicatesForOptions(options);
        const rawRecords = allFromSnapshot(await query.get());
        const filteredRecords = rawRecords
            .filter((record) => this.evaluatePredicatesForActionRecord(record, predicates))
            .map((recordWithMetadata) => recordWithMetadata.data);
        return { records: filteredRecords, unfilteredCount: rawRecords.length };
    }
    /** @inheritDoc */
    async get(documentUid, actionRecordUid) {
        return oneFromSnapshot(await this.db.documentActionRecordsCollection(documentUid).doc(actionRecordUid).get(), () => undefined)?.data;
    }
    /** @inheritDoc */
    async save(documentUid, actionRecord) {
        const metadata = this.metadataGenerator.generate("subcollection", this.clientId);
        return this.db
            .documentActionRecordsCollection(documentUid)
            .doc(actionRecord.actionRecordUid)
            .set({ metadata, data: actionRecord })
            .then();
    }
    /** @inheritDoc */
    async subscribeToNewActionRecordsForDocumentContext(documentUid, onChange) {
        const baseDocument = oneFromSnapshot(await this.db.document(documentUid).get(), () => undefined);
        // Get the timestamp of the record of the action last applied to the
        // document, or else use the current time.
        // QUESTION: Why is it important to go from the last action record for
        // "new"? The original code is undocumented, but I guess it is to cover
        // the case where the action records somehow get ahead of the document
        // state. This is currently expected in the short time window between
        // when an action record is saved and when the document state is saved.
        // See saveDocumentEpic. If another user loads a document at such a
        // point in time, they will be sure to get all needed action records to
        // bring their instance of the document up to date.
        let lastActionRecordTimestamp = new Date().getTime();
        if (baseDocument) {
            const latestActionRecordUid = baseDocument.documentData.latestActionRecordUid;
            if (latestActionRecordUid) {
                const latestActionRecord = await this.get(documentUid, latestActionRecordUid);
                if (latestActionRecord) {
                    lastActionRecordTimestamp = latestActionRecord.actionTimestamp;
                }
            }
        }
        // Get action records ordered ascending to apply them in the proper sequence
        return this.subscribeToNewActionRecords(documentUid, {
            range: lowerTimeBound(lastActionRecordTimestamp),
            ignoreClientWithId: this.clientId,
            // NOTE: This used to be `true`, but we now want to filter out
            // "version incrementing" records downstream. In brief, we want
            // multiplayer clients to apply all records, and change history
            // to filter.
            includeOnlyVersionIncrementingRecords: false,
        }, onChange);
    }
    /**
     * For change history only, not multiplayer clients.
     *
     * TODO: This should be refactored into a paginatedVersions type thing
     */
    async getDocumentVersionRelevantActionRecords(documentUid, startPointTS, limit = 50) {
        const queryResult = await this.getAll(documentUid, {
            maxResults: limit,
            range: upperTimeBound(startPointTS, false),
            timeOrder: "reverse",
            includeOnlyVersionIncrementingRecords: true,
        });
        // This is an unfortunate side effect of having client side predicates but server side limits.
        const cursorExhausted = queryResult.unfilteredCount < limit;
        const result = queryResult.records.reduce((acc, record) => {
            acc[record.actionRecordUid] = record;
            return acc;
        }, {});
        return { actionRecords: result, hasMore: !cursorExhausted };
    }
    /** @inheritDoc */
    subscribeToNewActionRecords(documentUid, options, onChange) {
        // Some of the options are used to compute server-side query constraints - that happens in this function call.
        const query = this.queryForOptions(documentUid, options);
        // These predicates are computed client side due to limitations in the way that firestore
        // queries work.  In particular, you can't have multiple inequality conditions, and there are some
        // details about how orderBy and where interact that make it difficult to implement these in the database.
        // So we don't!  Instead we have an additional client side processing step where we check these predicates
        // and only emit the action record for further processing if it passes them.
        const predicates = this.predicatesForOptions(options);
        return query.onSnapshot((snap) => {
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            snap.docChanges().forEach((change) => {
                if (change.type === "added") {
                    const record = change.doc.data();
                    if (this.evaluatePredicatesForActionRecord(record, predicates)) {
                        onChange({ type: "success", actionRecord: record.data });
                    }
                }
            });
        }, (error) => {
            onChange({ type: "error", error });
        });
    }
    evaluatePredicatesForActionRecord(actionRecord, predicates) {
        for (const predicate of predicates) {
            if (predicate(actionRecord) === false)
                return false;
        }
        return true;
    }
    predicatesForOptions(options) {
        // These predicates are computed client side due to limitations in the way that firestore
        // queries work.  In particular, you can't have multiple inequality conditions, and there are some
        // details about how orderBy and where interact that make it difficult to implement these in the database.
        // So we don't!  Instead we have an additional client side processing step where we check these predicates
        // and only emit the action record for further processing if it passes them.
        const storageIdPredicate = (ar) => {
            if (options.ignoreClientWithId) {
                return ar.metadata.storageId !== options.ignoreClientWithId;
            }
            return true;
        };
        const versionUpdatePredicate = (ar) => {
            if (options.includeOnlyVersionIncrementingRecords) {
                return ar.data.actionUpdatesDocumentVersion;
            }
            return true;
        };
        return [storageIdPredicate, versionUpdatePredicate];
    }
    /**
     * Constructs a firestore query based on the input options.  All of the voodoo that's done here is done
     * in the form of server-side (firebase) query constraints - none of the logic here is computed locally.
     */
    queryForOptions(documentUid, options) {
        let query = this.rangeQuery(documentUid, options);
        if (options.maxResults != null)
            query = query.limit(options.maxResults);
        return query;
    }
    rangeQuery(documentUid, options) {
        // Kind of irritating that there isn't a nicer way to write all of this, but so it goes.
        // It's extremely important that the orderBy appear in every clause... it can't come first
        // because Firestore.
        const ordering = options.timeOrder === "reverse" ? "desc" : "asc";
        const baseQuery = this.db.documentActionRecordsCollection(documentUid);
        if (!options.range)
            return baseQuery.orderBy("data.actionTimestamp", ordering);
        switch (options.range.type) {
            case "lower": {
                const lowerOp = options.range.lowerBound.inclusive ? ">=" : ">";
                return baseQuery
                    .where("data.actionTimestamp", lowerOp, options.range.lowerBound.timestamp)
                    .orderBy("data.actionTimestamp", ordering);
            }
            case "upper": {
                const upperOp = options.range.upperBound.inclusive ? "<=" : "<";
                return baseQuery
                    .where("data.actionTimestamp", upperOp, options.range.upperBound.timestamp)
                    .orderBy("data.actionTimestamp", ordering);
            }
            case "interval": {
                const lowerOp = options.range.lowerBound.inclusive ? ">=" : ">";
                const upperOp = options.range.upperBound.inclusive ? "<=" : "<";
                return baseQuery
                    .where("data.actionTimestamp", lowerOp, options.range.lowerBound.timestamp)
                    .where("data.actionTimestamp", upperOp, options.range.upperBound.timestamp)
                    .orderBy("data.actionTimestamp", ordering);
            }
        }
    }
}
//# sourceMappingURL=FirestoreActionRecordRepository.js.map